import { useDispatch } from 'react-redux';
import { MetadataMorePanel } from './MetadataMorePanel';
import {
  setMetadataElementColor,
  setMetadataElementIcon,
  removeMetadataElement,
} from '../../../../../../reducers/united-canvas/unitedCanvasReducer';
import { useCallback, useState } from 'react';
import { usePanels } from '../../../../../panels/usePanels';
import { MetadataDependenciesMenu } from './MetadataDependenciesMenu';
import { MetadataUsedByMenu } from './MetadataUsedByMenu';
import { MetadataMorePanelState } from './MetatadataMoreCtx';

export const ConnectedMetadataMorePanel = ({
  metadataMorePanelState,
  handleMetadataMoreClose,
  funnelMap,
}: {
  metadataMorePanelState: MetadataMorePanelState;
  handleMetadataMoreClose: () => void;
  funnelMap: FunnelMap;
}) => {
  const dispatch = useDispatch();
  const { color: selectedColor } =
    funnelMap.metadataElements[metadataMorePanelState.fullMetadataId];

  const onIconChange = useCallback(
    (iconName: string | null) => {
      if (!metadataMorePanelState?.fullMetadataId) {
        return;
      }
      dispatch(
        setMetadataElementIcon({
          fullMetadataId: metadataMorePanelState?.fullMetadataId,
          iconName,
        }),
      );
      handleMetadataMoreClose();
    },
    [metadataMorePanelState?.fullMetadataId, dispatch, handleMetadataMoreClose],
  );

  const onColorChange = useCallback(
    (color: string) => {
      dispatch(
        setMetadataElementColor({
          fullMetadataId: metadataMorePanelState?.fullMetadataId,
          color,
        }),
      );
      handleMetadataMoreClose();
    },
    [dispatch, metadataMorePanelState?.fullMetadataId, handleMetadataMoreClose],
  );
  const { maybeCloseActivePanel } = usePanels();

  const onRemoveClick = useCallback(() => {
    if (!metadataMorePanelState?.fullMetadataId) {
      return;
    }
    dispatch(removeMetadataElement({ fullMetadataId: metadataMorePanelState?.fullMetadataId }));
    handleMetadataMoreClose();
    maybeCloseActivePanel();
  }, [
    metadataMorePanelState?.fullMetadataId,
    dispatch,
    handleMetadataMoreClose,
    maybeCloseActivePanel,
  ]);

  const currentIcon = funnelMap.metadataElements?.[metadataMorePanelState?.fullMetadataId]?.icon;
  const [dependenciesMenuAnchor, setDependenciesMenuAnchor] = useState<
    (EventTarget & HTMLLIElement) | null
  >(null);
  const [usedByMenuAnchor, setUsedByMenuAnchor] = useState<(EventTarget & HTMLLIElement) | null>(
    null,
  );

  if (!metadataMorePanelState?.anchorEl) {
    return null;
  }

  return (
    <>
      <MetadataMorePanel
        anchorEl={metadataMorePanelState.anchorEl}
        handleClose={handleMetadataMoreClose}
        isOpen
        onIconChange={onIconChange}
        onColorChange={onColorChange}
        onRemoveClick={onRemoveClick}
        selectedColor={selectedColor}
        currentIcon={currentIcon}
        onDependenciesClick={(e) => setDependenciesMenuAnchor(e.currentTarget)}
        onUsedByClick={(e) => setUsedByMenuAnchor(e.currentTarget)}
      />
      {dependenciesMenuAnchor && (
        <MetadataDependenciesMenu
          anchorEl={dependenciesMenuAnchor}
          onClose={() => setDependenciesMenuAnchor(null)}
          funnelMap={funnelMap}
          fullMetadataId={metadataMorePanelState.fullMetadataId}
          closeAll={() => {
            setDependenciesMenuAnchor(null);
            handleMetadataMoreClose();
          }}
        />
      )}
      {usedByMenuAnchor && (
        <MetadataUsedByMenu
          anchorEl={usedByMenuAnchor}
          onClose={() => setUsedByMenuAnchor(null)}
          funnelMap={funnelMap}
          fullMetadataId={metadataMorePanelState.fullMetadataId}
          closeAll={() => {
            setUsedByMenuAnchor(null);
            handleMetadataMoreClose();
          }}
        />
      )}
    </>
  );
};
